import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { constants } from '@config';
import { AiImage } from '@desygner/ui-editor-ai-write';
import ReactAppBuilder from 'src/utils/react-app-builder';
import aiSamplePrompts from './ai-image-prompts';
import aiImageStyles from './ai-image-styles';
import { hasPlaceholderFrame } from 'assets/js/helpers/element/element-helper';

export const appContainer = 'ai-image-container';
class AiImageApp extends ReactAppBuilder {
  #outputFormats = {
    url: 'url',
    base64: 'base64'
  };

  #imageSize = {
    width: 1024,
    height: 1024
  };

  constructor ({ appId, targetNodeSelector }) {
    super({ appId, targetNodeSelector });

    this.outputFormat = this.#outputFormats.url;
  }

  mountApp () {
    super.mountApp(this.App);
  }

  closeApp () {
    super.closeApp(this.App);
  }

  handleBackButton = (setVisible, appNode) => {
    appNode && (appNode.style.width = '0');
    setVisible(false);

    setTimeout(() => this.closeApp(), 300);
  };

  handleImageInsertion = (imageSource) => {
    const selectedElements = INKIVE.DesignFacade.getSelectedElements();
    const hasImageSelected = selectedElements.length > 0 && INKIVE.DesignFacade.get(selectedElements[0].id).type === 'image';
    const isPlaceholderFrame = selectedElements?.[0] && hasPlaceholderFrame(selectedElements[0]);
    const url = this.outputFormat === 'base64' ? `data:image/png;base64, ${imageSource}` : imageSource;

    if (hasImageSelected || isPlaceholderFrame) {
      this.#replaceImage(selectedElements[0].id, url);
    } else {
      this.#createImage(url);
    }
  };

  #createImage = (url) => {
    const canvasWidth = INKIVE.EditorFacade.canvas.contentW;
    const canvasHeight = INKIVE.EditorFacade.canvas.contentH;

    /**
     * Images from AI are always square
     */
    const { width, height } = INKIVE.Tool.getInstance().getDimensionsSizeBasedOnSVGSize(canvasWidth, canvasWidth);

    INKIVE.DesignFacade.create('image', {
      imported: false,
      source: {
        external: this.outputFormat !== this.#outputFormats.base64,
        url,
        signed_url: true
      },
      position: {
        x: (canvasWidth - width) / 2,
        y: (canvasHeight - height) / 2
      },
      size: {
        width,
        height
      }
    });
  };

  #replaceImage (elementId, url) {
    const urlKey = this.outputFormat === this.#outputFormats.base64
      ? 'base_64'
      : 'url';

    /**
     * Open AI images are always 1024x1024
     */
    INKIVE.EditorFacade.canvas.runExtensions('addLayoutImage', {
      elem: document.getElementById(elementId),
      data: {
        url,
        width: this.#imageSize.width,
        height: this.#imageSize.height
      }
    });

    INKIVE.DesignFacade.set(elementId, {
      source: {
        external: this.outputFormat !== this.#outputFormats.base64,
        [urlKey]: url,
        signed_url: true
      },
      keep_box_size: true
    });
  }

  getTranslations = () => {
    return {
      cancelButton: INKIVE.TranslateHelper.translate('top_submenu.animation.confirm_split_cancel'),
      chooseStyle: INKIVE.TranslateHelper.translate('ai_write.app.choose_style'),
      clearButton: INKIVE.TranslateHelper.translate('ai_write.app.clear_cta'),
      errorMessage: INKIVE.TranslateHelper.translate('ai_write.app.error_message'),
      generateButton: INKIVE.TranslateHelper.translate('ai_write.app.generate_button'),
      generateButtonLoading: INKIVE.TranslateHelper.translate('ai_write.app.requestBtn_loading'),
      insertButton: INKIVE.TranslateHelper.translate('ai_write.app.insert_cta'),
      loadingMessage: INKIVE.TranslateHelper.translate('ai_image.app.loading_message'),
      loadingSlowMessage: INKIVE.TranslateHelper.translate('ai_write.app.loading_slow_message'),
      promptPlaceholder: INKIVE.TranslateHelper.translate('ai_image.app.placeholder'),
      regenerateButton: INKIVE.TranslateHelper.translate('ai_write.app.regenerate_button'),
      retrySampleButton: INKIVE.TranslateHelper.translate('ai_write.app.sample_retry'),
      sampleButton: INKIVE.TranslateHelper.translate('ai_write.app.sample_button'),
      seeMore: INKIVE.TranslateHelper.translate('ai_write.app.see_more'),
      subtitle: INKIVE.TranslateHelper.translate('ai_write.app.prompt'),
      title: INKIVE.TranslateHelper.translate('ai_image.title')
    };
  };

  App = () => {
    const [visible, setVisible] = useState(true);
    const appNode = document.getElementById(appContainer);
    const credentials = useSelector(state => state.user.userInfo.credentials);
    appNode && (appNode.style.width = '100%');

    const props = {
      visible,
      labels: this.getTranslations(),
      samplePrompts: aiSamplePrompts,
      imageStyles: aiImageStyles,
      outputFormat: this.outputFormat,
      apiBaseUrl: INKIVE.C.API_URL,
      company: CONFIG.company_id,
      userToken: credentials.api_key,
      insertNewAsset: this.handleImageInsertion.bind(this),
      handleBackButton: this.handleBackButton.bind(this, setVisible, appNode),
      loadingVideo: constants.loadingScreen.videoUrl
    };

    return <AiImage {...props} />;
  };
}

export default new AiImageApp({
  appId: appContainer,
  targetNodeSelector: '#tabImages'
});
